import camelize from "camelize-ts";
import { omit, pick } from "ramda";
import snakify from "snakify-ts";

/**
 * Performs camelization of object keys.
 * @param obj
 * @param shallow Default is deep camelize, otherwise camelize only the top-level keys
 * @param skipKeys If doing deep camelize, can specify which keys should be shallow camelized instead.
 *                 If doing shallow, can specify which keys should not be camelized at all.
 */
export const safelyCamelize = <T extends Record<string, any>>(
  obj: T | unknown,
  shallow = false,
  skipKeys: string[] = []
): any => {
  if (Array.isArray(obj)) {
    return obj.map((item) => safelyCamelize(item, shallow, skipKeys));
  }
  if (!obj || typeof obj !== "object") {
    return obj;
  }

  const typedObj = obj as Record<string, any>;
  const pickedObj = pick(skipKeys as (keyof typeof typedObj)[], typedObj) as Record<string, any>;
  const omittedObj = omit(skipKeys as (keyof typeof typedObj)[], typedObj) as Record<string, any>;

  const skipped = shallow ? pickedObj : (camelize(pickedObj, true) as Record<string, any>);

  const camelizedOmitted = camelize(omittedObj, shallow) as Record<string, any>;

  return {
    ...camelizedOmitted,
    ...skipped,
  };
};

export const partialSnakify = <T extends Record<string, any>>({
  data,
  omitKeys,
}: {
  data: T;
  omitKeys: string[];
}) => {
  const pickedData = pick(omitKeys as (keyof T)[], data) as Record<string, any>;
  const omittedData = omit(omitKeys as (keyof T)[], data) as Record<string, any>;
  const snakifiedData = snakify(omittedData) as Record<string, any>;

  return {
    ...pickedData,
    ...snakifiedData,
  };
};
